import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import get from 'lodash/get'

import Button from 'components.v2/Button'
import Text from 'components.v2/Text'
import OrderList from 'common/Order/OrderList'
import DiscountList from 'common/Order/DiscountList'
import withOrderContainer from 'pages/order/modules/withOrderContainer'
import ShadowScrollBox from 'components.v2/ShadowScrollBox'
import events from 'const/analytics-events'
import withAnalytics from 'enhancers/withAnalytics'

import {
  ordersToTotal,
  ordersToQty,
  discountToTotal,
} from 'pages/order/modules/order'
import { formatPrice } from 'utils/currency'
import { OrderBox, Description, OrderListContainer, Separator } from './styled'
import { isMultiVendor as isMultiVendorEnv } from 'utils/multiVendor'
import Icon from 'components.v2/Icon/IconV2'
import styled from 'styled-components'
import isEmpty from 'lodash/isEmpty'

const CheckoutButton = withAnalytics()(Button)
const ViewOrderGroupButton = withAnalytics()(styled.button`
  display: flex;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: var(--Green-0, #2e9a9a);
  height: 52px;

  &:focus {
    outline: none;
  }

  color: #fff;
  text-align: center;
  font-family: Montserrat;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px; /* 155.556% */
  letter-spacing: 0.18px;
`)

const Order = ({
  selectedOrders,
  match,
  discounts,
  venue,
  orderParty,
  history,
  isAuthenticated,
  isGfoOrder,
}) => {
  const quantity = ordersToQty(selectedOrders)
  const ordersTotal = ordersToTotal(selectedOrders)
  const discountTotal = discountToTotal(discounts)
  const subtotal = ordersTotal + discountTotal
  const hasDiscount = discounts && !!discounts.length
  const isMultiVendor = get(venue, 'isMultiVendor')

  const handleClick = () => {
    history.push(
      isMultiVendor && isMultiVendorEnv()
        ? `/order/review`
        : `/order/${match.params.id}/review${isGfoOrder ?
            '?channel=gfo_redirect' : ''}`,
    )
  }

  const gfoQueryParams = () => (isGfoOrder ? '?channel=gfo_redirect' : '')

  return (
    <OrderBox centered={selectedOrders.length === 0}>
      {selectedOrders.length === 0 ? (
        <div className="w-full px-15">
          <Text variant="h4" color="gray4" align="center">
            Your cart is empty
          </Text>
          <Description color="grey" align="center">
            Select something yummy from the menu and add it your order.
          </Description>
          {!isEmpty(orderParty) && isAuthenticated ? (
            <ViewOrderGroupButton
              onClick={handleClick}
              analytics={[
                {
                  eventType: 'onClick',
                  options: {
                    ...events['MENU_CLICK_GO_TO_CHECKOUT'],
                    label: String(match.params.id),
                    ...(isGfoOrder && {
                      gfo_order: true,
                    }),
                  },
                },
              ]}
            >
              <Icon
                className="flex items-center"
                src="orderParty/viewGroupOrder.svg"
              />
              View group order
            </ViewOrderGroupButton>
          ) : (
            <Button variant="secondary" disabled largeText>
              Go to checkout
            </Button>
          )}
        </div>
      ) : (
        <div style={{ width: '100%' }}>
          <Text variant="h4" className="mx-15 mb-20">
            Your order
          </Text>

          <ShadowScrollBox>
            <OrderListContainer className="px-15">
              <OrderList orders={selectedOrders} />
            </OrderListContainer>

            {hasDiscount && (
              <div className="mt-20">
                <DiscountList discounts={discounts} />
              </div>
            )}

            {!hasDiscount && <Separator />}
          </ShadowScrollBox>

          <div className="mx-15 my-20">
            {hasDiscount && (
              <div className="flex justify-between items-center">
                <Text variant="secondary" color="gray3">
                  Discount
                </Text>
                <Text variant="secondary" color="gray3">
                  -${discountTotal * -1}
                </Text>
              </div>
            )}

            <div className="flex justify-between items-center">
              <Text bold>
                Subtotal ({quantity} item{quantity > 1 ? 's' : ''})
              </Text>
              <Text>{formatPrice(subtotal)}</Text>
            </div>
          </div>

          {!isEmpty(orderParty) && isAuthenticated ? (
            <ViewOrderGroupButton
              className="mx-15"
              onClick={handleClick}
              analytics={[
                {
                  eventType: 'onClick',
                  options: {
                    ...events['MENU_CLICK_GO_TO_CHECKOUT'],
                    label: String(match.params.id),
                    ...(isGfoOrder && {
                      gfo_order: true,
                    }),
                  },
                },
              ]}
            >
              <Icon
                className="flex items-center"
                src="orderParty/viewGroupOrder.svg"
              />
              View group order
            </ViewOrderGroupButton>
          ) : (
            <CheckoutButton
              className="mx-15"
              as={Link}
              to={
                isMultiVendor && isMultiVendorEnv()
                  ? `/order/review`
                  : `/order/${match.params.id}/review${gfoQueryParams()}`
              }
              variant="secondary"
              analytics={[
                {
                  eventType: 'onClick',
                  options: {
                    ...events['MENU_CLICK_GO_TO_CHECKOUT'],
                    label: String(match.params.id),
                    ...(isGfoOrder && {
                      gfo_order: true,
                    }),
                  },
                },
              ]}
              largeText
            >
              Go to checkout
            </CheckoutButton>
          )}
        </div>
      )}
    </OrderBox>
  )
}

Order.propTypes = {
  selectedOrders: PropTypes.arrayOf(
    PropTypes.shape({
      quantity: PropTypes.number.isRequired,
    }),
  ).isRequired,
  discounts: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.string.isRequired,
    }),
  ).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
}

export default withOrderContainer({
  checkoutOnLoad: false,
  checkoutOnQuantityChange: false,
})(Order)
